import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, History, Info, User, X, Trash2, Clock, CheckCircle, RefreshCw } from 'lucide-react';

// Daily shift approval. Admin/PM approve or reject the scheduled roster (with
// actual in/out, total shift and break hours). On rejection the user must add a
// justification, which they do from their own view of this tab.
const ShiftApprovalSection = ({ token }) => {
  const todayIso = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayIso);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/shift-approvals', {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: d },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load shift roster');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(date); }, [load, date]);

  const isApprover = !!data?.is_approver;
  const rows = data?.rows || [];

  const approve = async (userId) => {
    setBusyId(userId || 'all');
    try {
      await api.post('/shift-approvals/approve',
        userId ? { date, user_id: userId } : { date },
        { headers: { Authorization: `Bearer ${token}` } });
      await load(date);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (userId) => {
    const reason = window.prompt('Reason for rejecting this shift (optional). The user will be asked to add a justification.');
    if (reason === null) return; // cancelled
    setBusyId(userId);
    try {
      await api.post('/shift-approvals/reject',
        { date, user_id: userId, reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      await load(date);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to reject');
    } finally {
      setBusyId(null);
    }
  };

  const justify = async (userId) => {
    const text = window.prompt('Add your justification for this rejected shift:');
    if (text === null) return;
    if (!text.trim()) { alert('Justification cannot be empty.'); return; }
    setBusyId(userId);
    try {
      await api.post('/shift-approvals/justify',
        { date, justification: text.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      await load(date);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit justification');
    } finally {
      setBusyId(null);
    }
  };

  const StatusBadge = ({ r }) => {
    if (r.status === 'approved') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="w-3 h-3" /> Approved
      </span>
    );
    if (r.status === 'rejected') return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        Rejected{r.justification ? ' · justified' : ''}
      </span>
    );
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Pending</span>;
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="text-sm text-gray-600">Date</label>
        <input
          type="date"
          value={date}
          max={todayIso}
          onChange={(e) => setDate(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={() => load(date)}
          className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        {data && (
          <span className="text-sm text-gray-500">
            {data.approved}/{data.total} approved · {data.pending} pending{data.rejected ? ` · ${data.rejected} rejected` : ''}
          </span>
        )}
        {isApprover && (
          <button
            onClick={() => approve(null)}
            disabled={busyId === 'all' || rows.length === 0 || (data && data.pending === 0)}
            className="ml-auto flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <CheckCircle className="w-4 h-4" /> Approve All
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="py-10 text-center text-gray-500 animate-pulse">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-500">
          {isApprover ? 'No users are scheduled for this date.' : 'You have no scheduled shift for this date.'}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-gray-600">
                {isApprover && <th className="px-4 py-2.5 font-semibold">User</th>}
                <th className="px-4 py-2.5 font-semibold">Shift</th>
                <th className="px-4 py-2.5 font-semibold">Scheduled</th>
                <th className="px-4 py-2.5 font-semibold">In</th>
                <th className="px-4 py-2.5 font-semibold">Out</th>
                <th className="px-4 py-2.5 font-semibold">Total Shift</th>
                <th className="px-4 py-2.5 font-semibold">Break</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={r.user_id} className="hover:bg-gray-50 align-top">
                  {isApprover && (
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {r.user_name}
                      <div className="text-xs text-gray-400 capitalize">{(r.role || '').replace(/_/g, ' ')}</div>
                    </td>
                  )}
                  <td className="px-4 py-3 text-gray-700">{r.shift_name || '-'}</td>
                  <td className="px-4 py-3 text-gray-600 font-mono">{r.shift_start} – {r.shift_end}</td>
                  <td className="px-4 py-3 text-gray-700 font-mono">{r.in_time || '—'}</td>
                  <td className="px-4 py-3 text-gray-700 font-mono">{r.out_time || '—'}</td>
                  <td className="px-4 py-3 text-gray-800 font-medium">{r.worked ? r.total_shift : '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{r.worked ? r.break_hours : '—'}</td>
                  <td className="px-4 py-3">
                    <StatusBadge r={r} />
                    {r.status === 'rejected' && r.reject_reason && (
                      <div className="text-xs text-red-600 mt-1 max-w-[220px]">Reason: {r.reject_reason}</div>
                    )}
                    {r.status === 'rejected' && r.justification && (
                      <div className="text-xs text-gray-600 mt-1 max-w-[220px]">Justification: {r.justification}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isApprover ? (
                      r.status === 'pending' ? (
                        <div className="flex justify-end gap-1.5">
                          <button onClick={() => approve(r.user_id)} disabled={busyId === r.user_id}
                            className="px-2.5 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50">Approve</button>
                          <button onClick={() => reject(r.user_id)} disabled={busyId === r.user_id}
                            className="px-2.5 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50">Reject</button>
                        </div>
                      ) : r.status === 'approved' ? (
                        <span className="text-xs text-gray-400">{r.approved_by_name ? `by ${r.approved_by_name}` : '—'}</span>
                      ) : (
                        <span className="text-xs text-gray-400">{r.rejected_by_name ? `by ${r.rejected_by_name}` : 'Rejected'}</span>
                      )
                    ) : (
                      r.status === 'rejected' && !r.justification ? (
                        <button onClick={() => justify(r.user_id)} disabled={busyId === r.user_id}
                          className="px-2.5 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50">Add justification</button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const TimeSheet = () => {
  const { token, user } = useAuth();
  const [timeEntries, setTimeEntries] = useState([]);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('daily'); // 'daily', 'weekly', 'monthly', 'custom'
  const [customStartDate, setCustomStartDate] = useState(''); // YYYY-MM-DD
  const [customEndDate, setCustomEndDate] = useState(''); // YYYY-MM-DD
  const [stats, setStats] = useState({
    workingHours: 0,
    breakHours: 0,
    totalHours: 0,
    utilization: 0,
    efficiency: 0
  });

  // TL/Admin features
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const isAdmin = user?.role === 'admin';
  const isPM = user?.role === 'pm';
  const isTeamLead = !!user?.role && ['preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead'].includes(user.role);
  // "Privileged" in this screen means: can view other users (admin, pm or team lead)
  const isPrivileged = isAdmin || isPM || isTeamLead;

  // Top-level tabs: timesheet vs. (admin-only) daily shift approval.
  const [mainTab, setMainTab] = useState('timesheet');
  // Show only late-comers (clocked in after their shift start + grace).
  const [lateOnly, setLateOnly] = useState(false);

  // User dropdown search/filter (match Reports -> User Time Tracking Summary)
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [userDropdownSearch, setUserDropdownSearch] = useState('');

  // Team filter for admin/PM
  const [selectedTeam, setSelectedTeam] = useState('');
  const [teams, setTeams] = useState([]);

  const getLeadTeamRole = (role) => {
    const map = {
      preflight_lead: 'preflight',
      cmd_lead: 'cmd',
      prepress_lead: 'prepress',
      qc_lead: 'qc',
      production_lead: 'production'
    };
    return map[role] || '';
  };

  const getBaseTeamFromRole = (role) => {
    if (!role) return '';
    if (role === 'admin' || role === 'pm') return '';
    if (role.endsWith('_lead')) return role.replace(/_lead$/, '');
    return role;
  };

  // Modal features
  const [showModal, setShowModal] = useState(false);
  const [modalActivities, setModalActivities] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  const [currentEntry, setCurrentEntry] = useState(null);
  const [modalStats, setModalStats] = useState(null);

  // End Shift Manual State
  const [openEndShiftDialog, setOpenEndShiftDialog] = useState(false);
  const [selectedUserForEndShift, setSelectedUserForEndShift] = useState(null);
  const [endShiftDate, setEndShiftDate] = useState('');
  const [endShiftTime, setEndShiftTime] = useState('');

  // Lateness reads as minutes up to an hour, then hours and minutes — "95m" is
  // harder to judge at a glance than "1h 35m".
  const formatLate = (minutes) => {
    const m = Math.max(0, Math.round(parseFloat(minutes) || 0));
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return rem ? `${h}h ${rem}m` : `${h}h`;
  };

  // Escalating shades so a 5-minute slip and a 2-hour one don't look the same.
  const lateBadgeClass = (minutes) => {
    const m = Math.max(0, Math.round(parseFloat(minutes) || 0));
    if (m >= 60) return 'bg-red-600 text-white';
    if (m >= 30) return 'bg-red-200 text-red-900';
    return 'bg-red-100 text-red-700';
  };

  const formatHoursHms = (hours) => {
    const value = parseFloat(hours);
    if (!Number.isFinite(value) || value <= 0) return '0h 0m 0s';
    let totalSeconds = Math.round(value * 3600);
    const h = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  const formatSecondsHms = (seconds) => {
    const value = parseFloat(seconds);
    if (!Number.isFinite(value) || value <= 0) return '0h 0m 0s';
    let totalSeconds = Math.round(value);
    const h = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  const breakLimits = {
    lunch: 45,
    dinner: 15,
    snacks: 15
  };

  const getBreakHoursByType = (entry, type) => {
    if (!entry || !type) return 0;
    return entry[`break_${type}`] || 0;
  };

  const isBreakOverLimit = (entry, type) => {
    const limitMinutes = breakLimits[type];
    if (!limitMinutes) return false;
    const minutes = getBreakHoursByType(entry, type) * 60;
    return minutes > limitMinutes;
  };

  const getBreakCellClass = (entry, type) => (
    isBreakOverLimit(entry, type) ? 'text-red-600 font-semibold' : 'text-gray-600'
  );

  const toDateOnlyString = (dateValue) => {
    const d = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  };

  const getTodayDateInput = () => new Date().toISOString().split('T')[0];

  const parseDateInputToLocalStart = (yyyyMmDd) => {
    if (!yyyyMmDd) return null;
    const d = new Date(`${yyyyMmDd}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const parseDateInputToLocalEnd = (yyyyMmDd) => {
    if (!yyyyMmDd) return null;
    const d = new Date(`${yyyyMmDd}T23:59:59.999`);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  useEffect(() => {
    if (token && isPrivileged) {
      fetchUsers();
    }
  }, [token, isPrivileged]);

  useEffect(() => {
    if (token) {
      fetchTimeEntries();
    }
  }, [token, viewMode, selectedUser, customStartDate, customEndDate, users, selectedTeam]);

  // Derive available teams from users for admin/PM
  useEffect(() => {
    if (isAdmin || isPM) {
      const teamSet = new Set();
      (Array.isArray(users) ? users : []).forEach(u => {
        const baseTeam = getBaseTeamFromRole(u.role);
        if (baseTeam) teamSet.add(baseTeam);
      });
      setTeams(Array.from(teamSet));
    } else {
      setTeams([]);
      setSelectedTeam('');
    }
  }, [users, isAdmin, isPM]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const allUsers = Array.isArray(response.data) ? response.data : [];

      // Admin / PM: can see everyone
      if (isAdmin || isPM) {
        setUsers(allUsers);
        return;
      }

      // Team lead: only see their team members (based on role mapping)
      if (isTeamLead) {
        const teamRole = getLeadTeamRole(user?.role);
        const allowedRoles = teamRole ? [teamRole, `${teamRole}_lead`] : [];
        const teamUsers = allowedRoles.length
          ? allUsers.filter(u => allowedRoles.includes(u.role))
          : [];
        setUsers(teamUsers);
        return;
      }

      // Default: no user picker
      setUsers([]);
    } catch (err) {
      console.error('Failed to fetch users', err);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      const now = new Date();
      let startDate = new Date();
      let endDate = new Date();

      if (viewMode === 'daily') {
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
      } else if (viewMode === 'weekly') {
        const day = startDate.getDay();
        const diff = startDate.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
        startDate.setDate(diff);
        startDate.setHours(0, 0, 0, 0);
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
      } else if (viewMode === 'monthly') {
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setMonth(startDate.getMonth() + 1);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
      } else if (viewMode === 'custom') {
        const start = parseDateInputToLocalStart(customStartDate);
        const end = parseDateInputToLocalEnd(customEndDate);

        // Initialize defaults if not set yet
        if (!customStartDate || !customEndDate) {
          const today = getTodayDateInput();
          if (!customStartDate) setCustomStartDate(today);
          if (!customEndDate) setCustomEndDate(today);
        }

        if (!start || !end) {
          setTimeEntries([]);
          calculateStats([]);
          return;
        }

        if (end.getTime() < start.getTime()) {
          setError('End date must be on/after start date');
          setTimeEntries([]);
          calculateStats([]);
          return;
        }

        // Use the selected custom range
        startDate = start;
        endDate = end;
      }

      setError(null);

      // Fetch Summary for Stats
      const baseParams = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };

      // Admin / PM:
      // - selectedUser => view that specific user
      // - selectedTeam (no selectedUser) => view all users in that team (including leads)
      // - otherwise => view all users
      if (isAdmin || isPM) {
        // Specific user selected
        if (selectedUser) {
          const params = { ...baseParams, userId: selectedUser };
          const summaryResponse = await api.get('/reports/time-tracking', {
            headers: { Authorization: `Bearer ${token}` },
            params
          });
          const entries = Array.isArray(summaryResponse.data) ? summaryResponse.data : [];
          setTimeEntries(entries);
          calculateStats(entries);
          return;
        }

        // Team selected: merge time entries for all users in that team
        if (selectedTeam) {
          const teamUserIds = (Array.isArray(users) ? users : [])
            .filter(u => getBaseTeamFromRole(u.role) === selectedTeam)
            .map(u => u.id)
            .filter(Boolean);

          if (teamUserIds.length === 0) {
            setTimeEntries([]);
            calculateStats([]);
            return;
          }

          const responses = await Promise.all(
            teamUserIds.map((userId) =>
              api.get('/reports/time-tracking', {
                headers: { Authorization: `Bearer ${token}` },
                params: { ...baseParams, userId }
              })
            )
          );

          const merged = responses
            .flatMap(r => (Array.isArray(r.data) ? r.data : []))
            .sort((a, b) => {
              const aDate = new Date(a.date).getTime();
              const bDate = new Date(b.date).getTime();
              if (aDate !== bDate) return bDate - aDate;
              return String(a.user_name || '').localeCompare(String(b.user_name || ''));
            });

          setTimeEntries(merged);
          calculateStats(merged);
          return;
        }

        // No user or team filter: show all users
        const params = { ...baseParams };
        const summaryResponse = await api.get('/reports/time-tracking', {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        const entries = Array.isArray(summaryResponse.data) ? summaryResponse.data : [];
        setTimeEntries(entries);
        calculateStats(entries);
        return;
      }

      // Team leads:
      // - selectedUser => only that team member
      // - no selectedUser => fetch all team members and merge
      if (isTeamLead) {
        if (selectedUser) {
          const summaryResponse = await api.get('/reports/time-tracking', {
            headers: { Authorization: `Bearer ${token}` },
            params: { ...baseParams, userId: selectedUser }
          });
          const entries = Array.isArray(summaryResponse.data) ? summaryResponse.data : [];
          setTimeEntries(entries);
          calculateStats(entries);
          return;
        }

        const teamUserIds = (Array.isArray(users) ? users : [])
          .map(u => u.id)
          .filter(Boolean);

        if (teamUserIds.length === 0) {
          setTimeEntries([]);
          calculateStats([]);
          return;
        }

        const responses = await Promise.all(
          teamUserIds.map((userId) =>
            api.get('/reports/time-tracking', {
              headers: { Authorization: `Bearer ${token}` },
              params: { ...baseParams, userId }
            })
          )
        );

        const merged = responses
          .flatMap(r => (Array.isArray(r.data) ? r.data : []))
          .sort((a, b) => {
            const aDate = new Date(a.date).getTime();
            const bDate = new Date(b.date).getTime();
            if (aDate !== bDate) return bDate - aDate;
            return String(a.user_name || '').localeCompare(String(b.user_name || ''));
          });

        setTimeEntries(merged);
        calculateStats(merged);
        return;
      }

      // Regular user: always scoped to self
      const selfId = user?.id;
      const params = { ...baseParams };
      if (selfId) params.userId = selfId;
      const summaryResponse = await api.get('/reports/time-tracking', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      const entries = Array.isArray(summaryResponse.data) ? summaryResponse.data : [];
      setTimeEntries(entries);
      calculateStats(entries);

    } catch (err) {
      setError('Failed to fetch time entries');
    }
  };

  const calculateStats = (entries) => {
    let totalWorking = 0;
    let totalBreak = 0;
    let totalOffice = 0;

    entries.forEach(entry => {
      totalWorking += entry.job_hours || 0; // Use job_hours for actual work
      totalBreak += entry.break_hours || 0;
      totalOffice += entry.total_shift_hours || 0;
    });

    const utilization = totalOffice > 0 ? (totalWorking / totalOffice) * 100 : 0;
    const efficiency = utilization; 

    setStats({
      workingHours: totalWorking,
      breakHours: totalBreak,
      totalHours: totalOffice,
      utilization: utilization.toFixed(1),
      efficiency: efficiency.toFixed(1)
    });
  };

  const handleShowDetails = async (entry) => {
    try {
      setCurrentEntry(entry);
      // Set modal title using entry's user info
      setModalTitle(`Activity Log - ${new Date(entry.date).toLocaleDateString()} (${entry.user_name || 'Unknown'})`);
      // Precompute per-user/day stats for the modal header (idle recalculated after events)
      const jobHours = entry.job_hours || 0;
      const breakHours = entry.break_hours || 0;
      const baseTotalShift = entry.total_shift_hours || 0;
      
      // Fetch detailed activities for this specific user and shift window.
      // Use the actual shift_start/shift_end times when available so that
      // events from the previous/next calendar day are not pulled into
      // this day's activity log.
      let rangeStart;
      let rangeEnd;

      if (entry.shift_start) {
        rangeStart = new Date(entry.shift_start);
      } else {
        rangeStart = new Date(entry.date);
        rangeStart.setHours(0, 0, 0, 0);
      }

      if (entry.shift_end && entry.shift_end !== 'Active') {
        rangeEnd = new Date(entry.shift_end);
      } else {
        // If the shift has not formally ended, cap the range at the
        // end of the calendar day or now, whichever is earlier.
        const endOfDay = new Date(entry.date);
        endOfDay.setHours(23, 59, 59, 999);
        const now = new Date();
        rangeEnd = now < endOfDay ? now : endOfDay;
      }

      const response = await api.get('/reports/activity-log', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: rangeStart.toISOString(),
          endDate: rangeEnd.toISOString(),
          userId: entry.user_id 
        }
      });
      
      let activities = Array.isArray(response.data) ? response.data : [];
      
      // Sort activities in ascending order (oldest first) so shift_start appears first
      activities.sort((a, b) => new Date(a.ts) - new Date(b.ts));
      
      // Calculate durations: only end-type events get a duration; starts show '-'
      let activitySeconds = 0;
      let currentStart = null;
      const endEvents = ['end', 'pause', 'hold', 'query', 'task_rejected', 'break_end', 'idle_end', 'shift_end'];
      const startEvents = ['start', 'resume', 'break_start', 'idle_start', 'shift_start'];
      const idleSecondsByType = {};

      const processedActivities = activities.map((activity) => {
        const ts = new Date(activity.ts);
        let duration = null;

        if (startEvents.includes(activity.event)) {
          currentStart = ts;
        } else if (endEvents.includes(activity.event) && currentStart) {
          duration = (ts - currentStart) / 1000;
          activitySeconds += duration;
          if (activity.event === 'idle_end' && activity.idle_type) {
            idleSecondsByType[activity.idle_type] = (idleSecondsByType[activity.idle_type] || 0) + duration;
          }
          currentStart = null;
        }

        return { ...activity, duration };
      });

      // Derive total shift seconds (prefer actual shift start/end if present)
      let totalShiftSeconds = 0;
      if (entry.shift_start) {
        const shiftStartTs = new Date(entry.shift_start).getTime();
        const shiftEndTs = (entry.shift_end && entry.shift_end !== 'Active') ? new Date(entry.shift_end).getTime() : Date.now();
        if (!Number.isNaN(shiftStartTs) && !Number.isNaN(shiftEndTs)) {
          totalShiftSeconds = Math.max(0, (shiftEndTs - shiftStartTs) / 1000);
        }
      }
      if (totalShiftSeconds === 0 && baseTotalShift) {
        totalShiftSeconds = baseTotalShift * 3600;
      }

      const idleSeconds = Math.max(0, totalShiftSeconds - activitySeconds);
      const totalShiftHours = (totalShiftSeconds / 3600) || 0;
      // Use pre-computed values from the entry (correctly calculated by backend)
      const idleHours = entry.idle_hours || 0;
      const noJobsIdleHours = entry.idle_no_jobs || 0;
      const nonTrackingHours = entry.non_tracking_hours || 0;
      const efficiency = totalShiftHours > 0 ? ((jobHours / totalShiftHours) * 100) : 0;

      setModalStats({
        jobHours,
        breakHours,
        idleHours: Number(idleHours).toFixed(2),
        idleNoJobsHours: Number(noJobsIdleHours).toFixed(2),
        nonTrackingHours: Number(nonTrackingHours).toFixed(2),
        totalShift: totalShiftHours.toFixed(2),
        efficiency: efficiency.toFixed(1)
      });

      setModalActivities(processedActivities);
      setShowModal(true);
    } catch (err) {
      console.error("Failed to fetch details", err);
      alert("Failed to fetch activity details");
    }
  };

  const formatEventName = (activity) => {
    const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
    const event = activity.event;
    if (event === 'break_start') return `${capitalize(activity.break_type) || 'Break'} Start`;
    if (event === 'break_end') return `${capitalize(activity.break_type) || 'Break'} End`;
    if (event === 'idle_start') return `${capitalize(activity.idle_type) || 'Idle'} Start`;
    if (event === 'idle_end') return `${capitalize(activity.idle_type) || 'Idle'} End`;
    
    return event.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return '-';
    return formatSecondsHms(seconds);
  };

  const getEventDescription = (activity) => {
    console.log('Activity full object:', JSON.stringify(activity, null, 2));
    if (activity.description) {
      console.log('Found description:', activity.description);
      return activity.description;
    }
    if (activity.job_title) return `Job: ${activity.job_title}`;
    if (activity.break_type) return `${activity.break_type} Break`;
    if (activity.idle_type) return `Idle: ${activity.idle_type}`;
    if (activity.notes) return activity.notes;
    return '-';
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await api.delete(`/time/event/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh details if modal is open
      if (currentEntry) {
        handleShowDetails(currentEntry);
      }
      // Refresh main list
      fetchTimeEntries();
    } catch (err) {
      console.error("Failed to delete event", err);
      alert("Failed to delete event");
    }
  };

  const handleOpenEndShiftDialog = (entry, dateOverride) => {
    setSelectedUserForEndShift(entry);
    const now = new Date();
    setEndShiftDate(dateOverride || now.toISOString().split('T')[0]);
    // Format time as HH:MM
    const timeString = now.toTimeString().slice(0, 5);
    setEndShiftTime(timeString);
    setOpenEndShiftDialog(true);
  };

  const submitEndShift = async () => {
    try {
      if (!selectedUserForEndShift || !endShiftDate || !endShiftTime) return;
      
      const dateTimeStr = `${endShiftDate}T${endShiftTime}:00`;
      const dateObj = new Date(dateTimeStr);
      
      await api.post(`/admin/users/${selectedUserForEndShift.user_id}/end-shift`, {
        shift_end_time: dateObj.toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOpenEndShiftDialog(false);
      setSelectedUserForEndShift(null);
      alert('Shift ended successfully');
      fetchTimeEntries();
    } catch (err) {
      console.error('Failed to end shift', err);
      alert('Failed to end shift: ' + (err.response?.data?.error || err.message));
    }
  };

  const visibleUsers = (isAdmin || isPM) && selectedTeam
    ? (Array.isArray(users) ? users : []).filter(u => getBaseTeamFromRole(u.role) === selectedTeam)
    : (Array.isArray(users) ? users : []);

  // Apply the "late comers only" filter to the rows shown in the table.
  const displayedEntries = lateOnly
    ? (Array.isArray(timeEntries) ? timeEntries : []).filter(e => e.is_late)
    : timeEntries;
  const lateEntries = (Array.isArray(timeEntries) ? timeEntries : []).filter(e => e.is_late);
  const lateCount = lateEntries.length;
  // Total minutes lost across the visible range, for the filter button.
  const lateMinutesTotal = lateEntries.reduce((sum, e) => sum + (parseFloat(e.minutes_late) || 0), 0);

  return (
    <div className="p-6 relative">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Time Sheet</h1>
        
        <div className="flex items-center gap-4">
          {(isAdmin || isPM) && (
            <div className="min-w-[180px] ">
              {/* <label className="block text-xs font-medium text-gray-600 mb-1">Team</label> */}
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Teams</option>
                {teams
                  .slice()
                  .sort()
                  .map(team => (
                    <option key={team} value={team}>
                      {team.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {(isAdmin || isPM || isTeamLead) && (
            <div className="relative min-w-[250px]" style={{ zIndex: 30 }}>
              <button
                type="button"
                onClick={() => setShowUserDropdown(v => !v)}
                className="w-full pl-3 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white flex items-center justify-between"
                title="Select User"
              >
                <span className="flex items-center gap-2 truncate">
                  <User className="text-gray-400 w-4 h-4" />
                  <span className="truncate">
                    {selectedUser
                      ? (users.find(u => String(u.id) === String(selectedUser))?.name || 'Selected User')
                      : ((isAdmin || isPM) ? 'All Users' : 'All Team Members')}
                  </span>
                </span>
                <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showUserDropdown && (
                <div className="absolute left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto z-30">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={userDropdownSearch}
                      onChange={(e) => setUserDropdownSearch(e.target.value)}
                      className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedUser('');
                        setUserDropdownSearch('');
                        setShowUserDropdown(false);
                      }}
                      className="w-full text-left px-3 py-1 text-sm hover:bg-gray-50"
                    >
                      {(isAdmin || isPM) ? 'All Users' : 'All Team Members'}
                    </button>
                    {visibleUsers
                      .filter(u =>
                        !userDropdownSearch ||
                        (u.name || '').toLowerCase().includes(userDropdownSearch.toLowerCase())
                      )
                      .map(u => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            setSelectedUser(String(u.id));
                            setShowUserDropdown(false);
                          }}
                          className={`w-full text-left px-3 py-1 text-sm hover:bg-gray-50 ${
                            String(u.id) === String(selectedUser) ? 'bg-blue-50 font-medium' : ''
                          }`}
                        >
                          {u.name}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex bg-white rounded-lg shadow-sm p-1 border border-gray-200">
            {['daily', 'weekly', 'monthly', 'custom'].map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-4 py-2 text-sm font-medium rounded-md capitalize transition-colors ${
                  viewMode === mode
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>

          {viewMode === 'custom' && (
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm p-2 border border-gray-200">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  max={customEndDate || undefined}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min={customStartDate || undefined}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
          )}

          <button
            onClick={() => setLateOnly(v => !v)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              lateOnly
                ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
            title={lateCount > 0
              ? `Show only late clock-ins (after shift start + 10 min grace). ${formatLate(lateMinutesTotal)} lost in total.`
              : 'Show only users who clocked in late (after shift start + 10 min grace)'}
          >
            <Clock className="w-4 h-4" />
            Late comers{lateCount > 0 ? ` (${lateCount} · ${formatLate(lateMinutesTotal)})` : ''}
          </button>
        </div>
      </div>

      {/* Main tabs: Timesheet vs. daily Shift Approval.
          Admin/PM use it to approve/reject; other users use it to add a
          justification to a rejected shift. */}
      <div className="flex gap-1 mb-5 border-b border-gray-200">
        <button
          onClick={() => setMainTab('timesheet')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            mainTab === 'timesheet' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          Timesheet
        </button>
        <button
          onClick={() => setMainTab('shift_approval')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            mainTab === 'shift_approval' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          Shift Approval
        </button>
      </div>

      {mainTab === 'shift_approval' ? (
        <ShiftApprovalSection token={token} />
      ) : (
      <>
      {error && (
        <div className="mb-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-blue-500">
          <div className="text-sm text-gray-500 mb-1">Job Hours</div>
          <div className="text-2xl font-bold text-gray-800">{formatHoursHms(stats.workingHours)}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-orange-500">
          <div className="text-sm text-gray-500 mb-1">Break Hours</div>
          <div className="text-2xl font-bold text-gray-800">{formatHoursHms(stats.breakHours)}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-green-500">
          <div className="text-sm text-gray-500 mb-1">Total Shift</div>
          <div className="text-2xl font-bold text-gray-800">{formatHoursHms(stats.totalHours)}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-purple-500">
          <div className="text-sm text-gray-500 mb-1">Efficiency</div>
          <div className="text-2xl font-bold text-gray-800">{stats.efficiency}%</div>
        </div>
      </div>

      {/* Summary Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <History className="w-5 h-5 mr-2 text-blue-600" />
            Shift Summary
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider">
                <th className="px-6 py-3 font-medium">Date</th>
                {isPrivileged && <th className="px-6 py-3 font-medium">User</th>}
                <th className="px-6 py-3 font-medium">Shift Start</th>
                <th className="px-6 py-3 font-medium">Shift End</th>
                <th className="px-6 py-3 font-medium">Total Shift</th>
                <th className="px-6 py-3 font-medium">Job Hrs</th>
                <th className="px-6 py-3 font-medium">Break Hrs</th>
                {/* <th className="px-6 py-3 font-medium">Lunch</th>
                <th className="px-6 py-3 font-medium">Snacks</th>
                <th className="px-6 py-3 font-medium">Dinner</th> */}
                <th className="px-6 py-3 font-medium">Idle Hrs</th>
                <th className="px-6 py-3 font-medium">No Jobs Hrs</th>
                <th className="px-6 py-3 font-medium">Non Tracking Hrs</th>
                <th className="px-6 py-3 font-medium text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {displayedEntries.map((entry, index) => (
                <tr key={entry.id || index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {new Date(entry.date).toLocaleDateString()}
                  </td>
                  {isPrivileged && (
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">
                      {entry.user_name}
                    </td>
                  )}
                  <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                    {entry.shift_start ? (
                      <span className="inline-flex items-center gap-1.5">
                        {new Date(entry.shift_start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {entry.is_late && (
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${lateBadgeClass(entry.minutes_late)}`}
                            title={entry.expected_shift_start
                              ? `Shift starts ${entry.expected_shift_start} — clocked in ${formatLate(entry.minutes_late)} late`
                              : 'Late'}
                          >
                            {entry.minutes_late ? `Late ${formatLate(entry.minutes_late)}` : 'Late'}
                          </span>
                        )}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                    {entry.shift_end && entry.shift_end !== 'Active' ? new Date(entry.shift_end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : (entry.shift_end || '-')}
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-gray-800">
                    {formatHoursHms(entry.total_shift_hours || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm text-green-600 font-medium">
                    {formatHoursHms(entry.job_hours || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatHoursHms(entry.break_hours || 0)}
                  </td>
                  {/*
                  <td className={`px-6 py-4 text-sm ${getBreakCellClass(entry, 'lunch')}`}> 
                    {formatHoursHms(getBreakHoursByType(entry, 'lunch'))}
                  </td>
                  <td className={`px-6 py-4 text-sm ${getBreakCellClass(entry, 'snacks')}`}> 
                    {formatHoursHms(getBreakHoursByType(entry, 'snacks'))}
                  </td>
                  <td className={`px-6 py-4 text-sm ${getBreakCellClass(entry, 'dinner')}`}> 
                    {formatHoursHms(getBreakHoursByType(entry, 'dinner'))}
                  </td>
                  */}
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatHoursHms(entry.idle_hours || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatHoursHms(entry.idle_no_jobs || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm text-red-500">
                    {formatHoursHms(entry.non_tracking_hours || 0)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex justify-center gap-2">
                      {!entry.shift_end && (
                        <button 
                          onClick={() => handleOpenEndShiftDialog(entry)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50 transition-colors"
                          title="End Shift"
                        >
                          <Clock className="w-5 h-5" />
                        </button>
                      )}
                      <button 
                        onClick={() => handleShowDetails(entry)}
                        className="text-green-600 hover:text-green-800 p-1 rounded-full hover:bg-green-50 transition-colors"
                        title="View Activity Log"
                      >
                        <Info className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {displayedEntries.length === 0 && (
                <tr>
                  <td colSpan={isPrivileged ? 11 : 10} className="px-6 py-8 text-center text-gray-500">
                    {lateOnly ? 'No late comers for this period' : 'No records found for this period'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {/* Activity Details Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-800">{modalTitle}</h3>
              <div className="flex items-center gap-2">
                {isPrivileged && currentEntry && (!currentEntry.shift_end || currentEntry.shift_end === 'Active') && (
                  <button
                    onClick={() => handleOpenEndShiftDialog(currentEntry, toDateOnlyString(currentEntry.date))}
                    className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-blue-50 transition-colors"
                    title="End Shift"
                  >
                    <Clock className="w-5 h-5" />
                  </button>
                )}
                <button 
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                  title="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {modalStats && (
              <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3 border-b border-gray-100 bg-gray-50">
                <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                  <div className="text-xs text-gray-500 mb-1">Total Shift</div>
                  <div className="text-lg font-semibold text-gray-800">{formatHoursHms(modalStats.totalShift)}</div>
                  <div className="text-xs text-gray-500">Efficiency: {modalStats.efficiency}%</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                  <div className="text-xs text-gray-500 mb-1">Job Hours</div>
                  <div className="text-lg font-semibold text-green-600">{formatHoursHms(modalStats.jobHours)}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                  <div className="text-xs text-gray-500 mb-1">Break Hours</div>
                  <div className="text-lg font-semibold text-gray-800">{formatHoursHms(modalStats.breakHours)}</div>
                  <div className="text-xs text-gray-500">(lunch/snacks/dinner)</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                  <div className="text-xs text-gray-500 mb-1">Idle Hrs</div>
                  <div className="text-lg font-semibold text-gray-800">{formatHoursHms(modalStats.idleHours)}</div>
                  <div className="text-xs text-gray-500">(Meeting/Call/Training)</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                  <div className="text-xs text-gray-500 mb-1">No Jobs Hrs</div>
                  <div className="text-lg font-semibold text-gray-800">{formatHoursHms(modalStats.idleNoJobsHours)}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                  <div className="text-xs text-gray-500 mb-1">Non Tracking Hrs</div>
                  <div className="text-lg font-semibold text-red-600">{formatHoursHms(modalStats.nonTrackingHours)}</div>
                </div>
              </div>
            )}
            
            <div className="flex-1 overflow-y-auto p-6">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider sticky top-0">
                    <th className="px-6 py-3 font-medium">Time</th>
                    <th className="px-6 py-3 font-medium">Activity</th>
                    <th className="px-6 py-3 font-medium">Description</th>
                    <th className="px-6 py-3 font-medium">Duration</th>
                    {(user?.role === 'admin') && <th className="px-6 py-3 font-medium">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {modalActivities.map((activity, index) => (
                    <tr key={activity.id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                        {new Date(activity.ts).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          activity.event.includes('start') || activity.event.includes('resume') ? 'bg-green-100 text-green-800' :
                          activity.event.includes('end') || activity.event.includes('pause') ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {formatEventName(activity)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {getEventDescription(activity)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                        {formatDuration(activity.duration)}
                      </td>
                      {(user?.role === 'admin') && (
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {activity.event === 'shift_end' && (
                            <button 
                              onClick={() => handleDeleteEvent(activity.id)}
                              className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                              title="Delete Event"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {modalActivities.length === 0 && (
                    <tr>
                      <td colSpan={user?.role === 'admin' ? 5 : 4} className="px-6 py-8 text-center text-gray-500">
                        No detailed activities found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button 
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* End Shift Dialog */}
      {openEndShiftDialog && selectedUserForEndShift && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                End Shift for {selectedUserForEndShift.user_name}
              </h3>
              <button 
                onClick={() => setOpenEndShiftDialog(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600">
                Please specify the date and time when this user's shift ended. This will clock them out effective at the specified time.
              </p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shift End Date</label>
                <input
                  type="date"
                  value={endShiftDate}
                  onChange={(e) => setEndShiftDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shift End Time</label>
                <input
                  type="time"
                  value={endShiftTime}
                  onChange={(e) => setEndShiftTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setOpenEndShiftDialog(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitEndShift}
                disabled={!endShiftDate || !endShiftTime}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                End Shift
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeSheet;
